import React from 'react';
import { Input as AntInput, type InputProps as AntInputProps, ConfigProvider } from 'antd';

import commonStyles from '../../style/components/common.module.scss';

import { createPrimaryTheme, createSecondaryTheme } from '../../theme/antd-theme';
import type { MultilanguageErrorState, MultilanguageState } from '../../hooks/useMultilanguageState';
import { controlSpaces, removeAllSpaces } from '../../helpers/spaceControl';

export type InputTheme = 'primary' | 'secondary';

interface CustomInputProps {
  label?: string;
  theme?: InputTheme;
  error?: boolean | MultilanguageErrorState;
  errorMessage?: string;
  isTextArea?: boolean;
  onChange: (value: string) => void;
  prefixIcon?: React.ReactNode;
  required?: boolean;
  maxLength?: number;
  attempted?: boolean;
  showCount?: boolean;
  currentLanguage?: string;
  value: string | number | MultilanguageState;
  controlSpaces?: boolean;
  noSpaces?: boolean;
}

interface InputProps extends Omit<AntInputProps, 'value' | 'onChange' | 'showCount'>, CustomInputProps {}

const Input: React.FC<InputProps> = ({
  label,
  theme = 'primary',
  error,
  errorMessage,
  isTextArea = false,
  className = '',
  onChange,
  prefixIcon,
  required = false,
  maxLength,
  showCount = false,
  value,
  attempted,
  currentLanguage,
  controlSpaces: enableSpaceControl = false,
  noSpaces: enableNoSpaces = false,
  ...props
}) => {
  const textAreaSafeProps = {
    placeholder: props.placeholder,
    disabled: props.disabled,
    style: props.style,
    id: props.id,
    name: props.name,
    readOnly: props.readOnly,
  };

  const getThemeConfig = () => {
    switch (theme) {
      case 'secondary':
        return createSecondaryTheme();
      default:
        return createPrimaryTheme();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let inputValue = e.target.value;

    // Apply space control based on props
    if (enableNoSpaces) {
      // Remove all spaces for UDID fields
      inputValue = removeAllSpaces(inputValue);
    } else if (enableSpaceControl) {
      // Control spaces (max 1 space) for other fields
      inputValue = controlSpaces(inputValue);
    }

    if (typeof value === 'object' && currentLanguage) {
      const updatedValue = { ...(value as MultilanguageState), [currentLanguage]: inputValue };
      onChange(updatedValue as unknown as string);
      return;
    }
    onChange(inputValue);
  };

  const getCurrentValue = () => {
    if (typeof value === 'object' && currentLanguage) {
      return value[currentLanguage] || '';
    }
    return String(value);
  };

  const currentLength = getCurrentValue().length;
  const isOverLimit = maxLength ? currentLength > maxLength : false;

  const hasError = typeof error === 'object' ? (currentLanguage ? !error[currentLanguage] : false) : error;

  return (
    <ConfigProvider theme={getThemeConfig()}>
      <div className={commonStyles.container}>
        {label && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <label className={commonStyles.label}>
              {label} {required && <span style={{ color: 'var(--color-success)' }}>*</span>}
            </label>
            {showCount && maxLength && (
              <span
                style={{
                  fontSize: '12px',
                  color: isOverLimit ? '#ff4d4f' : '#666',
                  fontWeight: isOverLimit ? '500' : 'normal',
                }}
              >
                {currentLength}/{maxLength}
              </span>
            )}
          </div>
        )}

        {isTextArea ? (
          <AntInput.TextArea
            autoSize={{ minRows: 3 }}
            onChange={handleChange}
            value={typeof value === 'object' && currentLanguage ? value[currentLanguage] : value}
            maxLength={maxLength}
            {...textAreaSafeProps}
            status={hasError ? 'error' : undefined}
          />
        ) : (
          <AntInput
            prefix={prefixIcon}
            {...props}
            value={typeof value === 'object' && currentLanguage ? value[currentLanguage] : value}
            onChange={handleChange}
            status={hasError ? 'error' : undefined}
            maxLength={maxLength}
          />
        )}

        {hasError && <span className={commonStyles.errorMessage}>{errorMessage || 'This field is mandatory.'} </span>}
      </div>
    </ConfigProvider>
  );
};

export default Input;

